import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Link,
    Tab,
    Tabs,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
} from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiHelpCircleOutline } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import {
    fetchLoadTimePickupParties,
    fetchUnloadTimeDeliveryParties,
    // updateLoadTimePickupPartiesApiParams,
    // updateUnloadTimeDeliveryPartiesApiParams,
} from '../../state/features/networkHealthSlice';
import { privateRoutes } from '../../routes/appRouteList';
import { Consignee } from '../../interfaces/services/consignees';
import { Shipper } from '../../interfaces/services/shippers';
import { ApiStatus, Grade } from '../../utils/enums';
import { buildShipperHealthLink } from '../../utils/navigationHelpers';
import { formatNumber } from '../../utils/formattingUtils';
import { getGradeColor } from '../../utils/styleUtils';
import GradeLabel from '../Labels/GradeLabel';

const classesPrefix = 'loadTimeCard';

const classes = {
    cardHeader: `${classesPrefix}-cardHeader`,
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    tableWrapper: `${classesPrefix}-tableWrapper`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledCard = styled(Card)(({ theme }) => {
    return {
        [`& .${classes.cardHeader}`]: {
            paddingBottom: theme.spacing(0),
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: theme.palette.action.hover,
            },
        },
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
        [`& .${classes.tableWrapper}`]: {
            marginTop: theme.spacing(1),
            height: '244px',
            '& .loadTimeTable.gradeA': {
                color: getGradeColor(Grade.A, theme),
            },
            '& .loadTimeTable.gradeB': {
                color: getGradeColor(Grade.B, theme),
            },
            '& .loadTimeTable.gradeC': {
                color: getGradeColor(Grade.C, theme),
            },
            '& .loadTimeTable.gradeD': {
                color: getGradeColor(Grade.D, theme),
            },
            '& .loadTimeTable.gradeE': {
                color: getGradeColor(Grade.E, theme),
            },
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

enum LoadTimeTabs {
    Shippers,
    Consignees
}

const LoadTimeCard = (): React.JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [currentTab, setCurrentTab] = useState(LoadTimeTabs.Shippers);
    // TODO: TCSC-50464
    const [shipperPage, setShipperPage] = useState(0);
    const [shipperPageSize, setShipperPageSize] = useState(10);
    const [consigneePage, setConsigneePage] = useState(0);
    const [consigneePageSize, setConsigneePageSize] = useState(10);

    const unloadTimeDeliveryPartiesApiStatus = useAppSelector((state) => { return state.networkHealth.unloadTimeDeliveryPartiesApiStatus; });
    // const unloadTimeDeliveryPartiesApiParams = useAppSelector((state) => { return state.networkHealth.unloadTimeDeliveryPartiesApiParams; }); // TODO: TCSC-50464
    const unloadTimeDeliveryParties = useAppSelector((state) => { return state.networkHealth.unloadTimeDeliveryParties; });

    const loadTimePickupPartiesApiStatus = useAppSelector((state) => { return state.networkHealth.loadTimePickupPartiesApiStatus; });
    // const loadTimePickupPartiesApiParams = useAppSelector((state) => { return state.networkHealth.loadTimePickupPartiesApiParams; }); // TODO: TCSC-50464
    const loadTimePickupParties = useAppSelector((state) => { return state.networkHealth.loadTimePickupParties; });

    useEffect(() => {
        if (currentTab === LoadTimeTabs.Shippers) {
            // TODO: TCSC-50464
            // dispatch(fetchLoadTimePickupParties(loadTimePickupPartiesApiParams));
            dispatch(fetchLoadTimePickupParties());
        }
    }, [dispatch, currentTab]);

    useEffect(() => {
        if (currentTab === LoadTimeTabs.Consignees) {
            // TODO: TCSC-50464
            // dispatch(fetchUnloadTimeDeliveryParties(unloadTimeDeliveryPartiesApiParams));
            dispatch(fetchUnloadTimeDeliveryParties());
        }
    }, [dispatch, currentTab]);

    const loadTimeTooltip = useMemo((): React.JSX.Element => {
        return (
            <div>
                <Typography variant='body2' gutterBottom className={classes.helpText}>
                    <span className={classes.title}>Load Time</span>
                    <span> is the number of hours spent at a pickup location.</span>
                </Typography>
                <Typography variant='body2' className={classes.helpText}>
                    The grade thresholds for Load Time are based on the amount of time from arrival at the pickup location to departing the pickup location:
                </Typography>
                <ul>
                    <li>
                        <span className={classes.title}>A</span>
                        <span> = Less than 30 minutes</span>
                    </li>
                    <li>
                        <span className={classes.title}>C</span>
                        <span> = Between 30 minutes and 2 hours</span>
                    </li>
                    <li>
                        <span className={classes.title}>E</span>
                        <span> = Greater than 2 hours</span>
                    </li>
                </ul>
            </div>
        );
    }, []);

    const unloadTimeTooltip = useMemo((): React.JSX.Element => {
        return (
            <div>
                <Typography variant='body2' gutterBottom className={classes.helpText}>
                    <span className={classes.title}>Unload Time</span>
                    <span> is the number of hours spent at a delivery location.</span>
                </Typography>
                <Typography variant='body2' className={classes.helpText}>
                    The grade thresholds for Unload Time are  based on the amount of time from arrival at the delivery location to departing the delivery location:
                </Typography>
                <ul>
                    <li>
                        <span className={classes.title}>A</span>
                        <span> = Less than 30 minutes</span>
                    </li>
                    <li>
                        <span className={classes.title}>C</span>
                        <span> = Between 30 minutes and 2 hours</span>
                    </li>
                    <li>
                        <span className={classes.title}>E</span>
                        <span> = Greater than 2 hours</span>
                    </li>
                </ul>
            </div>
        );
    }, []);

    const shipperColumns = useMemo((): GridColDef<Shipper>[] => {
        return [
            {
                headerName: 'Shipper',
                field: 'pickup_party_name',
                sortable: false,
                hideSortIcons: true,
                flex: 1,
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip title={row.pickup_party_name}>
                            <Link
                                component={RouterLink}
                                to={buildShipperHealthLink(row.pickup_party_id)}
                            >
                                {row.pickup_party_name}
                            </Link>
                        </Tooltip>
                    );
                },
            },
            {
                headerName: 'Load Time',
                field: 'average_load_hours_duration',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip title={loadTimeTooltip}>
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-50464
                valueGetter: (_value, row): number | null => {
                    return row.load_hours_duration !== null && row.freight_order_count ?
                        row.load_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: ({ row }): string => {
                    return `loadTimeTable grade${row.average_load_hours_duration_grade}`;
                },
            },
        ];
    }, [loadTimeTooltip]);

    const consigneeColumns = useMemo((): GridColDef<Consignee>[] => {
        return [
            {
                headerName: 'Consignee',
                field: 'delivery_party_name',
                sortable: false,
                hideSortIcons: true,
                flex: 1,
            },
            {
                headerName: 'Unload Time',
                field: 'average_unload_hours_duration',
                sortable: false,
                hideSortIcons: true,
                type: 'number',
                renderHeader: (params): React.JSX.Element => {
                    return (
                        <StyledTooltip title={unloadTimeTooltip}>
                            <span>{params.colDef.headerName}</span>
                        </StyledTooltip>
                    );
                },
                // TODO: TCSC-50464
                valueGetter: (_value, row): number | null => {
                    return row.unload_hours_duration !== null && row.freight_order_count ?
                        row.unload_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                cellClassName: ({ row }): string => {
                    return `loadTimeTable grade${row.average_unload_hours_duration_grade}`;
                },
            },
        ];
    }, [unloadTimeTooltip]);

    const handleShippersPaginationModelChange = (model: GridPaginationModel): void => {
        // TODO: TCSC-50464
        // dispatch(updateLoadTimePickupPartiesApiParams({
        //     // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
        //     pageNumber: model.page + 1,
        //     pageSize: model.pageSize,
        // }));

        setShipperPage(model.page);
        setShipperPageSize(model.pageSize);
    };

    const handleConsigneesPaginationModelChange = (model: GridPaginationModel): void => {
        // TODO: TCSC-50464
        // dispatch(updateLoadTimeDeliveryPartiesApiParams({
        //     // MUI has 0 based paging, and our API is 1 based, so we need to add a 1 here.
        //     pageNumber: model.page + 1,
        //     pageSize: model.pageSize,
        // }));

        setConsigneePage(model.page);
        setConsigneePageSize(model.pageSize);
    };

    const handleLoadTimeCardClick = (): void => {
        navigate(privateRoutes.shipperHealth);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
        setCurrentTab(newValue);
    };

    return (
        <StyledCard>
            <CardHeader
                classes={{
                    root: classes.cardHeader,
                    content: classes.cardHeaderContent,
                }}
                title={
                    <Fragment>
                        <Typography variant='h4'>
                            {currentTab === LoadTimeTabs.Shippers ? 'Load Time' : 'Unload Time'}
                        </Typography>
                        <GradeLabel
                            apiStatus={
                                currentTab === LoadTimeTabs.Shippers ?
                                    loadTimePickupPartiesApiStatus :
                                    unloadTimeDeliveryPartiesApiStatus
                            }
                            grade={
                                currentTab === LoadTimeTabs.Shippers ?
                                    loadTimePickupParties.pickup_party_health.load_hours_duration_grade :
                                    unloadTimeDeliveryParties.delivery_party_health.unload_hours_duration_grade
                            }
                            variant='h2'
                        />
                    </Fragment>
                }
                disableTypography
                action={
                    <StyledTooltip
                        title={currentTab === LoadTimeTabs.Shippers ? loadTimeTooltip : unloadTimeTooltip}
                    >
                        <IconButton size='small' color='primary'>
                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                        </IconButton>
                    </StyledTooltip>
                }
                onClick={handleLoadTimeCardClick}
            />

            <CardContent className={classes.cardContent}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label='Shipper' value={LoadTimeTabs.Shippers} />
                    <Tab label='Consignee' value={LoadTimeTabs.Consignees} />
                </Tabs>

                <div className={classes.tableWrapper}>
                    {
                        currentTab === LoadTimeTabs.Shippers &&
                        <DataGridPremium
                            rows={loadTimePickupParties.pickup_parties}
                            columns={shipperColumns}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            disableRowGrouping
                            disableAggregation
                            loading={loadTimePickupPartiesApiStatus === ApiStatus.Idle || loadTimePickupPartiesApiStatus === ApiStatus.Loading}
                            getRowId={(row): string => {
                                return row.pickup_party_id;
                            }}
                            // rowCount={loadTimePickupParties.pickup_party_count}
                            pagination
                            // paginationMode='server'
                            paginationModel={{
                                // TODO: TCSC-50464
                                // // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                                // page: loadTimePickupPartiesApiParams.pageNumber - 1,
                                // pageSize: loadTimePickupPartiesApiParams.pageSize,
                                page: shipperPage,
                                pageSize: shipperPageSize,
                            }}
                            onPaginationModelChange={handleShippersPaginationModelChange}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            initialState={{
                                density: 'compact',
                                // TODO: TCSC-50464
                                sorting: {
                                    sortModel: [
                                        { field: 'average_load_hours_duration', sort: 'desc' },
                                        { field: 'pickup_party_name', sort: 'asc' },
                                    ],
                                },
                            }}
                        />
                    }

                    {
                        currentTab === LoadTimeTabs.Consignees &&
                        <DataGridPremium
                            rows={unloadTimeDeliveryParties.delivery_parties}
                            columns={consigneeColumns}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            disableRowGrouping
                            disableAggregation
                            loading={unloadTimeDeliveryPartiesApiStatus === ApiStatus.Idle || unloadTimeDeliveryPartiesApiStatus === ApiStatus.Loading}
                            getRowId={(row): string => {
                                return row.delivery_party_id;
                            }}
                            // rowCount={unloadTimeDeliveryParties.delivery_party_count}
                            pagination
                            // paginationMode='server'
                            paginationModel={{
                                // TODO: TCSC-50464
                                // // MUI has 0 based paging, and our API is 1 based, so we need to subtract a 1 here.
                                // page: unloadTimeDeliveryPartiesApiStatus.pageNumber - 1,
                                // pageSize: unloadTimeDeliveryPartiesApiStatus.pageSize,
                                page: consigneePage,
                                pageSize: consigneePageSize,
                            }}
                            onPaginationModelChange={handleConsigneesPaginationModelChange}
                            pageSizeOptions={[5, 10, 25, 50, 100]}
                            initialState={{
                                density: 'compact',
                                // TODO: TCSC-50464
                                sorting: {
                                    sortModel: [
                                        { field: 'average_unload_hours_duration', sort: 'desc' },
                                        { field: 'pickup_party_name', sort: 'asc' },
                                    ],
                                },
                            }}
                        />
                    }
                </div>
            </CardContent>
        </StyledCard>
    );
};

export default LoadTimeCard;
